import { create } from 'zustand';

import { useAuthStore } from './auth.store';
import type { IVerifyUser } from './auth.types';
import { isViewingAs } from './view-as';

// Booking CSV exports carry full client data: Super Admin (`Admin`) and
// Company Admin only. The booking API checks the role again. Never while
// viewing as another role: `POST /auth/export-confirmation` refuses it.
const EXPORT_ROLES = ['Admin', 'Company Admin'];

const canExportCsv = (user: IVerifyUser | undefined) =>
  EXPORT_ROLES.includes(user?.role?.name ?? '') && !isViewingAs();

// Stop using a token a little before the server does, so a download started
// at the last second does not bounce off a 403.
const EXPIRY_MARGIN_MS = 5000;

type ExportTokenState = {
  token?: string;
  expiresAt?: number;
  // The token belongs to the session it was confirmed in.
  accessToken?: string;
  save: (token: string, expiresInSeconds: number) => void;
  clear: () => void;
};

// In memory on purpose, never persisted: a reload just asks for a new code.
const useExportTokenStore = create<ExportTokenState>()(set => ({
  token: undefined,
  expiresAt: undefined,
  accessToken: undefined,
  save: (token, expiresInSeconds) =>
    set({
      token,
      expiresAt: Date.now() + expiresInSeconds * 1000,
      accessToken: useAuthStore.getState().access_token,
    }),
  clear: () =>
    set({ token: undefined, expiresAt: undefined, accessToken: undefined }),
}));

/** The confirmed export token, while it is still good for this session. */
const getValidExportToken = () => {
  const { token, expiresAt, accessToken } = useExportTokenStore.getState();

  if (!token || !expiresAt) return undefined;
  if (accessToken !== useAuthStore.getState().access_token) return undefined;
  if (Date.now() >= expiresAt - EXPIRY_MARGIN_MS) return undefined;

  return token;
};

export { canExportCsv, getValidExportToken, useExportTokenStore };
